import Chip from 'components/@extended/Chip';
import AuditInfo from 'components/audit/AuditInfo';
import DisplayHistoryButton from 'components/buttons/DisplayHistoryButton';
import CustomFieldDisplay from 'components/forms/inputs/custom-fields/CustomFieldDisplay';
import { AddressPencilButton } from 'components/forms/inputs/rhf-inputs/AddressInput';
import EmployeeName from 'components/employee/EmployeeName';
import FeatureFlagged from 'components/featureflag/FeatureFlagged';
import GridItemList, {
    FIELD_GRID_CLASS,
    FIELD_LABEL_CLASS,
    GridCells,
    infoLabel,
    Row,
    SectionCard,
    spansRow,
} from 'components/GridItemList';
import InfoTooltip from 'components/InfoTooltip';
import { EllipsisTooltip } from 'components/misc/EllipsisTooltip';
import { useModal } from 'components/modals/useModal';
import { PersonalCodeValue } from 'components/PersonalCodeShow';
import { EmployeeNameLink } from 'components/record-chip/RecordLink';
import { Avatar, AvatarFallback, AvatarImage } from 'components/ui/avatar';
import { Button } from 'components/ui/button';
import { EditOutlined, Lock as LockIcon } from 'components/icons';
import { DraftSharedWith } from 'components/drafts/DraftSharedWith';
import { DraftActionsContext } from 'components/drafts/useDraftActions';
import { withMissing } from 'components/drafts/withMissing';
import { RECORD_GRID_CLASS } from 'components/record-header/RecordHeader';
import { format } from 'date-fns';
import { useEffectiveDateFormat } from 'hooks/useEffectiveDateFormat';
import { Fragment, ReactNode, useContext, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useLazyGetEmployeePersonalIdCodeQuery } from 'store/api/employees';
import { ClassifierData } from 'types/classifiers';
import { CustomField } from 'types/customFields';
import { Employee } from 'types/employees';
import { FeatureFlag } from 'types/features';
import { HistoryEntityType, HistoryFieldType } from 'types/histories';
import { getAddress, getOptionsFromClassifier } from 'utils/formUtil';
import { getClassifierText, t } from 'utils/getTranslations';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';
import { displayName, useLastNameFirst } from 'utils/nameOrder';
import { isPersonalCodeMask } from 'utils/personalCodeMask';
import { formatPracticeAreaLabel } from 'utils/practiceAreaUtil';
import { useTimecardPrecision } from 'utils/useTimecardPrecision';
import EditTargetsModal from 'pages/dashboard/progress-tracking/EditTargetsModal';
import { describeDerivedTargets, formatHours, NO_VALUE } from 'pages/dashboard/progress-tracking/progressCalculation';
import { useProgressMetric } from 'pages/dashboard/progress-tracking/progressMetric';
import { getLinkChips } from 'pages/matters/util';
import AffiliationUnit from '../affiliations/AffiliationUnit';
import { EmployeeSectionId, employeeSectionGates } from '../employeeFormMapping';
import { EmploymentBreak } from '../employment/EmploymentBreak';
import { EmploymentUnit } from '../employment/EmploymentUnit';
import { EmployeeSection } from '../form-sections/EmployeeSection';
import { useEmployeePermissions } from '../useEmployeePermissions';
import { useWebIntegrationRows } from './useWebIntegrationRows';
import { getEmploymentGapLabel, mapEmploymentDtoToForm, sortEmploymentHistoryByValidFrom } from '../util';

interface Props {
    employee: Employee;
    classifiers: Record<string, ClassifierData>;
    // Custom field classifier codes are tenant-defined, so they resolve against every classifier.
    allClassifiers: ClassifierData[];
    customFields: CustomField[];
    profilePictureSrc?: string;
    /** Shown in the new design only, which reads the signature on the page. */
    signaturePictureSrc?: string;
    /** The new design: a card per topic, each editing its own fields in place. */
    isNew?: boolean;
}

export function formatDateOrDash(value: string | null | undefined, pattern: string) {
    if (!value) return '-';
    try {
        return format(new Date(value), pattern);
    } catch {
        return '-';
    }
}

// `entity-link` takes the detail row's link colour (index.css).
const LINK_CLASS = 'entity-link text-fg hover:text-link w-fit no-underline hover:underline';

export function linkifyList<T extends { id: number; name: string }>(items: T[] | null | undefined, base: string) {
    if (!items || items.length === 0) return '-';
    return (
        <div className="flex flex-col">
            {items.map((item) => (
                <EmployeeNameLink className={LINK_CLASS} key={item.id} to={`/${base}/${item.id}`} entity={item} />
            ))}
        </div>
    );
}

/** The values of a multi-select as the chips its field draws in a card: links, under the names the field shows. */
const linkChips = <T extends { id: number; name: string }>(
    items: T[] | null | undefined,
    base: string,
    lastNameFirst: boolean
) =>
    getLinkChips(
        base,
        (items ?? []).map((item) => ({ id: item.id, name: displayName(item, lastNameFirst) }))
    );

/** A titled card laid out like a `GridItemList`'s, around content that is not label/value rows. */
function HistoryCard({ titleId, children }: { titleId: string; children: ReactNode }) {
    return (
        <section className="border-divider bg-paper new:border-0 new:shadow-card new:outline-hidden overflow-hidden rounded-[10px] border">
            <div className="border-divider border-b px-4 py-3 text-base leading-6 font-semibold">
                <FormattedMessage id={titleId} />
            </div>
            {children}
        </section>
    );
}

export function GeneralView({
    employee,
    classifiers,
    allClassifiers,
    customFields,
    profilePictureSrc,
    signaturePictureSrc,
    isNew = false,
}: Props) {
    const modalActions = useModal();
    const dateFormat = useEffectiveDateFormat();
    const {
        isSelf,
        canReadAllPersonalData,
        canReadPersonalData,
        canSeeAllRoles,
        hasReadRolePermission,
        canReadPartnerPeerGroup,
        hasUpdateEmployees,
        canUpdatePersonalData,
        hasUpdatePartnerPeerGroupPermission,
        hasUpdateRolesPermission,
        canUpdateAdministrativeRoles,
        canEditImpersonation,
        canShowPersonalIdCode,
    } = useEmployeePermissions(employee.id);
    const [getPersonalIdCode] = useLazyGetEmployeePersonalIdCodeQuery();
    const lastNameFirst = useLastNameFirst();
    const webIntegrationRows = useWebIntegrationRows(employee, lastNameFirst);
    const showExternalId = useIsFeatureEnabled(FeatureFlag.EXTERNAL_EMPLOYEE_ID_ON_COLLEAGUE_PROFILE_SOR);
    const showLocalNames = useIsFeatureEnabled(FeatureFlag.LOCALIZED_FIELDS);
    const showSignature = useIsFeatureEnabled(FeatureFlag.EMPLOYEE_SIGNATURE_FILE);
    const showVatRegNum = useIsFeatureEnabled(FeatureFlag.EMPLOYEE_VAT_REG_NUM);
    const showSignedBy = useIsFeatureEnabled(FeatureFlag.SIGNED_BY_ON_INVOICE_INSTEAD);
    const showTargets = useIsFeatureEnabled(FeatureFlag.DASHBOARD_PROGRESS_TRACKING) && canReadAllPersonalData;
    const metric = useProgressMetric({ skip: !showTargets });
    const { displayDecimalScale } = useTimecardPrecision();

    const initials =
        (lastNameFirst
            ? [employee.lastName?.[0], employee.firstName?.[0]]
            : [employee.firstName?.[0], employee.lastName?.[0]]
        )
            .filter(Boolean)
            .join('') || '?';

    // Where the roles field locks the administrative roles, for the new design's chips to carry the same lock.
    const locksAdministrativeRoles = canSeeAllRoles && hasUpdateRolesPermission && !canUpdateAdministrativeRoles;

    // `chips` and `readOnly` are read by the new design's cards only: a multi-select's values as its field draws
    // them, and the rows no field of the card edits.
    const rows = useMemo(() => {
        // A limited reader does not see the administrative roles.
        const shownRoles = (employee.systemRoles ?? []).filter(
            (role) => canSeeAllRoles || role.administrativeRole !== true
        );
        return {
            picture: {
                label: t('employees.profile-picture'),
                content: (
                    <Avatar className="size-36">
                        <AvatarImage src={profilePictureSrc} />
                        <AvatarFallback className="bg-grey-200 text-fg text-[54px]">{initials}</AvatarFallback>
                    </Avatar>
                ),
            },
            title: {
                label: t('title'),
                content: getClassifierText(classifiers['PERSON_TITLE'], employee.personTitleCode) || '-',
            },
            isActive: {
                label: (
                    <div className="flex items-center gap-[.5ch]">
                        {t(isNew ? 'active' : 'employees.is-active')}
                        <DisplayHistoryButton
                            entityType={HistoryEntityType.EMPLOYEE}
                            entityId={`${employee.id}`}
                            fieldType={HistoryFieldType.IS_ACTIVE}
                            fieldName={t('employees.is-active')}
                            activity={t('updating')}
                        />
                    </div>
                ),
                content: employee.isActive ? t('yes') : t('no'),
            },
            cognitoId: {
                label: t('employees.cognito-id'),
                // The cognito id is populated once the person completes account setup. A null value means the
                // employee record exists but no auth user has been provisioned yet — flag it instead of showing '-'.
                content: employee.cognitoId ? (
                    <span className="break-all">{employee.cognitoId}</span>
                ) : (
                    <Chip type="statusWarning" size="small" label={t('employees.user-not-initialized')} />
                ),
                readOnly: true,
            },
            externalId: {
                label: t('employees.external-id'),
                content: employee.externalId || '-',
                readOnly: true,
            },
            systemRoles: {
                label: t('employees.system-roles'),
                // A limited reader whose employee has only administrative roles ends up with an empty
                // string after the filter — the `|| '-'` keeps the row from looking like a bug.
                content: shownRoles.map((role) => role.name).join(', ') || '-',
                chips: shownRoles.map((role) => ({
                    key: role.id,
                    label: role.name,
                    icon:
                        locksAdministrativeRoles && role.administrativeRole === true ? (
                            <LockIcon fontSize="small" />
                        ) : undefined,
                })),
            },
            email: {
                label: t('employees.work-email-address'),
                content: employee.workEmailAddress ? (
                    <a className={LINK_CLASS} href={`mailto:${employee.workEmailAddress}`}>
                        {employee.workEmailAddress}
                    </a>
                ) : (
                    '-'
                ),
            },
            mobilePhone: {
                label: t('employees.mobile-phone'),
                content: employee.mobilePhone || '-',
            },
            workPhone: {
                label: t('employees.work-phone'),
                content: employee.workPhone || '-',
            },
            supervisor: {
                label: t('supervisor'),
                content: employee.manager?.id ? (
                    <EmployeeNameLink
                        className={LINK_CLASS}
                        to={`/employee/${employee.manager.id}`}
                        entity={employee.manager}
                    />
                ) : (
                    '-'
                ),
            },
            startDate: {
                label: t('employees.employment-start-date'),
                content: formatDateOrDash(employee.employmentStartDate, dateFormat),
            },
            endDate: {
                label: t('employees.employment-end-date'),
                content: formatDateOrDash(employee.employmentLeaveDate, dateFormat),
            },
            notes: {
                label: t('employees.notes'),
                content: employee.notes || '-',
                multiline: true,
            },
            previousNames: {
                label: t('employees.previous-names'),
                content: employee.previousNames || '-',
                multiline: true,
            },
            tasksSharedWith: {
                label: t('employees.tasks-shared-with'),
                content: linkifyList(employee.taskReceivers, 'employee'),
                chips: linkChips(employee.taskReceivers, 'employee', lastNameFirst),
            },
            tasksSharedFor: {
                label: t('employees.tasks-shared-for'),
                content: linkifyList(employee.receivingTasksForEmployees, 'employee'),
                readOnly: true,
            },
            canImpersonate: {
                label: t('employees.can-impersonate'),
                content: linkifyList(employee.canImpersonate, 'employee'),
                chips: linkChips(employee.canImpersonate, 'employee', lastNameFirst),
                // The form has the field for those who may set impersonation only.
                readOnly: !canEditImpersonation,
            },
            canBeImpersonatedBy: {
                label: t('employees.can-be-impersonated-by'),
                content: linkifyList(employee.canBeImpersonatedBy, 'employee'),
                readOnly: true,
            },
        } satisfies Record<string, Row>;
    }, [
        employee,
        classifiers,
        canSeeAllRoles,
        locksAdministrativeRoles,
        canEditImpersonation,
        lastNameFirst,
        profilePictureSrc,
        initials,
        dateFormat,
        isNew,
    ]);

    // On a draft, the values it was saved without read as missing, and whom it is shared with shows. A draft has no
    // account until it is created, so it has no sign-in id to show.
    const draftActions = useContext(DraftActionsContext);
    const missingFields = draftActions?.draft?.missingFields;
    const sharedWith = draftActions ? <DraftSharedWith /> : null;
    const cognitoIdRows = draftActions ? [] : [rows.cognitoId];

    const overviewRows: Row[] = [
        rows.picture,
        rows.title,
        rows.isActive,
        ...cognitoIdRows,
        ...(showExternalId ? [rows.externalId] : []),
        ...(hasReadRolePermission ? [rows.systemRoles] : []),
    ];

    const contactRows: Row[] = [
        rows.email,
        rows.mobilePhone,
        rows.workPhone,
        rows.supervisor,
        rows.startDate,
        rows.endDate,
        rows.notes,
        rows.previousNames,
        rows.tasksSharedWith,
        rows.tasksSharedFor,
        rows.canImpersonate,
        rows.canBeImpersonatedBy,
    ];

    // `chips` are the new design's: each multi-select's values in the form's order.
    const competenceRows: Row[] = useMemo(() => {
        const languageLabels = (employee.languagesSpoken ?? []).flatMap((code) => {
            const label = getClassifierText(classifiers['LANGUAGE'], code);
            return label ? [label] : [];
        });
        return [
            {
                label: t('employees.support-teams'),
                content: linkifyList(employee.supportTeams, 'team'),
                chips: linkChips(employee.supportTeams, 'team', lastNameFirst),
            },
            {
                label: t('employees.chairman-teams'),
                content: linkifyList(employee.chairmanTeams, 'team'),
                chips: linkChips(employee.chairmanTeams, 'team', lastNameFirst),
            },
            {
                label: t('practice-areas.practice-areas'),
                content: employee.practiceAreas?.length ? (
                    <div className="flex flex-col">
                        {employee.practiceAreas.map((pa) => (
                            <span key={pa.id} className="text-body1">
                                {formatPracticeAreaLabel(pa)}
                            </span>
                        ))}
                    </div>
                ) : (
                    '-'
                ),
                chips: (employee.practiceAreas ?? []).map((pa) => ({ key: pa.id, label: formatPracticeAreaLabel(pa) })),
            },
            {
                label: t('employees.languages-spoken'),
                content: languageLabels.length > 0 ? languageLabels.join(', ') : '-',
                // In the classifier's order, as the form holds them.
                chips: getOptionsFromClassifier<string, string>(classifiers['LANGUAGE'])
                    .filter((option) => (employee.languagesSpoken ?? []).includes(option.value))
                    .map((option) => ({ key: option.value, label: option.label })),
            },
        ];
    }, [employee, classifiers, lastNameFirst]);

    const customFieldRows: Row[] = useMemo(
        () =>
            customFields.map((field) => ({
                label: field.label ? (field.shouldTranslate ? t(field.label) : field.label) : '',
                content: <CustomFieldDisplay field={field} classifiers={allClassifiers} />,
            })),
        [customFields, allClassifiers]
    );

    const personalRows: Row[] = useMemo(() => {
        if (!canReadPersonalData) return [];
        const rows: Row[] = [];
        if (canReadAllPersonalData) {
            rows.push({
                label: t('employees.gender'),
                content: getClassifierText(classifiers['GENDER_TYPE'], employee.genderTypeCode ?? undefined) || '-',
            });
        }
        rows.push({
            label: t('employees.personal-id-code'),
            content: isPersonalCodeMask(employee.personalIdCode) ? (
                <PersonalCodeValue
                    key={employee.id}
                    code={employee.personalIdCode}
                    canShow={canShowPersonalIdCode}
                    request={() => getPersonalIdCode(String(employee.id))}
                />
            ) : (
                employee.personalIdCode || '-'
            ),
        });
        if (canReadAllPersonalData) {
            const countryLabel = employee.personalAddress
                ? getClassifierText(classifiers['COUNTRY'], employee.personalAddress.countryCode)
                : undefined;
            const address = employee.personalAddress ? getAddress(employee.personalAddress, countryLabel) : '-';
            rows.push(
                {
                    label: t('employees.date-of-birth'),
                    content: formatDateOrDash(employee.birthdayDate, dateFormat),
                },
                {
                    label: isNew
                        ? infoLabel('employees.sensitive-info', 'employees.sensitive-info-tooltip')
                        : t('employees.sensitive-info'),
                    content: employee.sensitiveInfo || '-',
                    multiline: true,
                },
                {
                    label: t('employees.politically-exposed-person'),
                    // `isPoliticallyExposedPerson` is `boolean | null`; null means the field was never
                    // answered — show '-' rather than implying a definitive "no".
                    content:
                        employee.isPoliticallyExposedPerson === null
                            ? '-'
                            : employee.isPoliticallyExposedPerson
                              ? t('yes')
                              : t('no'),
                },
                {
                    label: isNew
                        ? infoLabel('employees.personal-address', 'employees.personal-address-tooltip')
                        : t('employees.personal-address'),
                    // In the new design a set address has a row of its own and wraps on it as its field does:
                    // with the room of the field's pencil after its last word.
                    content: isNew ? (
                        <>
                            {address}
                            <AddressPencilButton aria-hidden disabled className="invisible" />
                        </>
                    ) : (
                        address
                    ),
                    wide: spansRow(address),
                }
            );
        }
        return rows;
    }, [
        employee,
        classifiers,
        canReadAllPersonalData,
        canReadPersonalData,
        canShowPersonalIdCode,
        getPersonalIdCode,
        dateFormat,
        isNew,
    ]);

    const targetRows: Row[] = useMemo(() => {
        const { annualTargetHours, plannedWorkdaysOff } = employee;
        const derived = describeDerivedTargets(
            annualTargetHours,
            plannedWorkdaysOff,
            displayDecimalScale,
            new Date().getFullYear()
        );
        return [
            {
                label: (
                    <div className="flex items-center gap-1">
                        {t('dashboard.progress-tracking.annual-target-with-metric', { metric: t(metric.phraseId) })}
                        <InfoTooltip text={metric.tooltipId} />
                    </div>
                ),
                content: typeof annualTargetHours === 'number' ? formatHours(annualTargetHours, 0) : NO_VALUE,
            },
            {
                label: (
                    <div className="flex items-center gap-1">
                        {t('dashboard.progress-tracking.planned-workdays-off')}
                        <InfoTooltip text="dashboard.progress-tracking.planned-workdays-off-tooltip" />
                    </div>
                ),
                content: typeof plannedWorkdaysOff === 'number' ? String(plannedWorkdaysOff) : NO_VALUE,
            },
            {
                label: t('dashboard.progress-tracking.planned-workdays-per-year'),
                content: derived.plannedWorkdaysPerYear,
            },
            { label: t('dashboard.progress-tracking.daily-target-per-working-day'), content: derived.dailyTarget },
        ];
    }, [employee, displayDecimalScale, metric]);

    // Sort newest-first; null validFrom sorts to the bottom. The BE serializes validFrom as an
    // ISO-8601 string, so it must be parsed as a date before comparing.
    const sortedEmploymentHistory = useMemo(
        () => sortEmploymentHistoryByValidFrom((employee.employmentHistory ?? []).map(mapEmploymentDtoToForm)),
        [employee]
    );

    // Everyone edits their own targets here; other employees' go through the edit form, or in the new design
    // through the Targets card's Edit.
    const targetsModalButton = isSelf ? (
        <Button
            variant="outline"
            size="xs"
            className="text-fg"
            onClick={() =>
                modalActions.openModal(
                    <EditTargetsModal
                        onClose={modalActions.closeModal}
                        annualTargetHours={employee.annualTargetHours}
                        plannedWorkdaysOff={employee.plannedWorkdaysOff}
                    />,
                    modalActions.closeModal
                )
            }
        >
            <EditOutlined className="size-3.5" />
            <FormattedMessage id="dashboard.progress-tracking.edit-targets" />
        </Button>
    ) : undefined;

    const peerGroupRow: Row = {
        label: (
            <div className="flex items-center gap-[.5ch]">
                {t('employees.partnership-peer-group')}
                <DisplayHistoryButton
                    entityType={HistoryEntityType.EMPLOYEE}
                    entityId={`${employee.id}`}
                    fieldType={HistoryFieldType.PARTNERSHIP_PEER_GROUP_TYPE_CODE}
                    fieldName={t('employees.partnership-peer-group')}
                    activity={t('updating')}
                />
            </div>
        ),
        content:
            getClassifierText(
                classifiers['PARTNERSHIP_PEER_GROUP_TYPE'],
                employee.partnershipPeerGroupTypeCode ?? undefined
            ) || '-',
    };

    const employmentUnits = sortedEmploymentHistory.map((employment, index) => {
        const newerEmployment = sortedEmploymentHistory[index - 1];
        const gapLabel = newerEmployment ? getEmploymentGapLabel(employment.validTo, newerEmployment.validFrom) : null;
        return (
            <Fragment key={employment.recordId}>
                {gapLabel && <EmploymentBreak label={gapLabel} />}
                <EmploymentUnit
                    employment={employment}
                    readonly
                    jobRoleList={[]}
                    jobPositionList={[]}
                    modalActions={modalActions}
                />
            </Fragment>
        );
    });

    const affiliationUnits = employee.affiliations.map((aff, index) => (
        <AffiliationUnit
            // oxlint-disable-next-line react-doctor/no-array-index-as-key -- affiliation has no stable id
            key={`${aff.party?.id ?? 'aff'}-${index}`}
            affiliation={{ ...aff, error: false }}
            readonly
            modalActions={modalActions}
        />
    ));

    const auditInfo = (
        <div className="md:col-span-2">
            <AuditInfo
                createdAt={employee.createdAt}
                createdByEmployee={employee.createdByEmployee}
                lastModifiedAt={employee.lastModifiedAt}
                changedByEmployee={employee.changedByEmployee}
            />
        </div>
    );

    if (isNew) {
        const gates = employeeSectionGates({
            isSelf,
            hasUpdateEmployees,
            canUpdatePersonalData,
            canReadAllPersonalData,
            hasUpdatePartnerPeerGroupPermission,
        });
        const card = (id: EmployeeSectionId, titleId: string, data: Row[], button?: ReactNode) => (
            <EmployeeSection id={id} titleId={titleId} employee={employee} canEdit={gates[id]} rows={data}>
                {(edit) => (
                    <GridItemList
                        isNew
                        titleId={titleId}
                        data={withMissing(data, missingFields)}
                        button={edit ?? button}
                    />
                )}
            </EmployeeSection>
        );
        const listCard = (id: EmployeeSectionId, titleId: string, units: ReactNode[]) => (
            <EmployeeSection id={id} titleId={titleId} employee={employee} canEdit={gates[id]}>
                {(edit) => (
                    <SectionCard title={<FormattedMessage id={titleId} />} action={edit}>
                        {units.length > 0 ? <div>{units}</div> : <span className="text-muted-foreground">-</span>}
                    </SectionCard>
                )}
            </EmployeeSection>
        );
        const nameRow = (label: string, value: string | null) => ({ label: t(label), content: value || '-' });
        const firstNameRows: Row[] = [
            nameRow('first-name', employee.firstName),
            ...(showLocalNames ? [nameRow('first-name-local', employee.localFirstName)] : []),
        ];
        const lastNameRows: Row[] = [
            nameRow('last-name', employee.lastName),
            ...(showLocalNames ? [nameRow('last-name-local', employee.localLastName)] : []),
        ];
        const identityRows: Row[] = [
            ...(lastNameFirst ? [...lastNameRows, ...firstNameRows] : [...firstNameRows, ...lastNameRows]),
            rows.title,
            rows.previousNames,
        ];
        // A picture where the identity form's image field has it, in that field's box: the pictures are side by
        // side on a row of their own, at the size they are edited at, so the rows under them stay put on Edit.
        // The field sets its picture on a line of text, which leaves the room under the line's baseline below
        // it; so does this.
        const picture = (label: string, image: ReactNode) => (
            <div className="py-2">
                <dt className={FIELD_LABEL_CLASS}>{label}</dt>
                <dd className="m-0 mt-1">
                    <div className="inline-block max-w-full overflow-hidden">{image}</div>
                </dd>
            </div>
        );
        const identityCard = (
            <EmployeeSection
                id="identity"
                titleId="employees.identity"
                employee={employee}
                canEdit={gates.identity}
                rows={identityRows}
            >
                {(edit) => (
                    <SectionCard title={<FormattedMessage id="employees.identity" />} action={edit}>
                        <div className={FIELD_GRID_CLASS}>
                            <dl className="col-span-full m-0 flex flex-wrap gap-x-5">
                                {picture(
                                    t('employees.profile-picture'),
                                    <Avatar className="size-[150px]">
                                        <AvatarImage src={profilePictureSrc} />
                                        <AvatarFallback className="bg-primary-tint text-primary text-[54px] font-semibold">
                                            {initials}
                                        </AvatarFallback>
                                    </Avatar>
                                )}
                                {showSignature
                                    ? picture(
                                          t('employees.signature-picture'),
                                          <div className="text-foreground flex h-[150px] w-[300px] max-w-full items-center text-sm font-medium">
                                              {signaturePictureSrc ? (
                                                  <img
                                                      src={signaturePictureSrc}
                                                      alt={t('employees.signature-picture')}
                                                      className="max-h-full max-w-full object-contain"
                                                  />
                                              ) : (
                                                  '-'
                                              )}
                                          </div>
                                      )
                                    : null}
                            </dl>
                            <dl className="contents">
                                <GridCells rows={identityRows} />
                            </dl>
                        </div>
                    </SectionCard>
                )}
            </EmployeeSection>
        );
        // Each card lists its form's fields in the form's order; the rows no field edits come last.
        const accountRows: Row[] = [
            rows.isActive,
            ...(hasReadRolePermission ? [rows.systemRoles] : []),
            rows.canImpersonate,
            ...cognitoIdRows,
            ...(showExternalId ? [rows.externalId] : []),
            rows.canBeImpersonatedBy,
        ];
        const employmentRows: Row[] = [
            rows.supervisor,
            ...(showVatRegNum ? [nameRow('employees.employee-vat-reg-num', employee.vatRegNum)] : []),
            ...(showSignedBy
                ? [
                      {
                          label: t('employees.overwrite-signed-by-employee-id'),
                          content: employee.overwriteSignedByEmployeeId ? (
                              // One line, as the field's own value is.
                              <EllipsisTooltip>
                                  <EmployeeName entity={employee.overwriteSignedByEmployeeId} />
                              </EllipsisTooltip>
                          ) : (
                              '-'
                          ),
                      },
                  ]
                : []),
            rows.startDate,
            rows.endDate,
            rows.notes,
            rows.tasksSharedWith,
            rows.tasksSharedFor,
        ];
        return (
            <div className={RECORD_GRID_CLASS}>
                <div className="flex min-w-0 flex-col gap-(--card-gap)">
                    {identityCard}
                    {card('account', 'employees.account-and-access', accountRows)}
                    {card('contact-details', 'contacts.contact-details', [
                        rows.email,
                        rows.mobilePhone,
                        rows.workPhone,
                    ])}
                    {card('employment', 'employees.employment-title', employmentRows)}
                    {canReadPersonalData && personalRows.length > 0
                        ? card('personal', 'employees.personal', personalRows)
                        : null}
                    {showTargets ? card('targets', 'employees.targets', targetRows, targetsModalButton) : null}
                    {webIntegrationRows
                        ? card('web-integration', 'employees.web.integration', webIntegrationRows)
                        : null}
                </div>
                <div className="flex min-w-0 flex-col gap-(--card-gap)">
                    {sharedWith}
                    {card('competence', 'employees.competence', competenceRows)}
                    {canReadPartnerPeerGroup ? (
                        <FeatureFlagged action="show" flag={FeatureFlag.PARTNER_PEER_GROUP}>
                            {card('peer-group', 'employees.partnership-peer-group', [peerGroupRow])}
                        </FeatureFlagged>
                    ) : null}
                    {listCard('employment-history', 'employees.employment-history', employmentUnits)}
                    {listCard('affiliations', 'employees.affiliations-and-external-roles', affiliationUnits)}
                    {customFieldRows.length > 0 ? card('custom-fields', 'custom-fields.title', customFieldRows) : null}
                </div>
                {auditInfo}
            </div>
        );
    }

    return (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div className="flex flex-col gap-6">
                <GridItemList
                    titleId="employees.general"
                    size={12}
                    data={withMissing(overviewRows, missingFields)}
                    key="employee-overview"
                />
                <GridItemList
                    titleId="contacts.contact-details"
                    size={12}
                    data={withMissing(contactRows, missingFields)}
                    key="employee-contact"
                />
                {canReadPersonalData && personalRows.length > 0 ? (
                    <GridItemList
                        titleId={showTargets ? 'employees.personal-info' : 'employees.personal'}
                        size={12}
                        data={personalRows}
                        key="employee-personal"
                    />
                ) : null}
                {showTargets ? (
                    <GridItemList
                        titleId="employees.targets"
                        data={targetRows}
                        key="employee-targets"
                        button={targetsModalButton}
                    />
                ) : null}
                {webIntegrationRows ? (
                    <GridItemList
                        titleId="employees.web.integration"
                        size={12}
                        data={webIntegrationRows}
                        key="employee-web-integration"
                    />
                ) : null}
            </div>
            <div className="flex flex-col gap-6">
                {sharedWith}
                <GridItemList
                    titleId="employees.competence"
                    size={12}
                    data={withMissing(competenceRows, missingFields)}
                    key="employee-competence"
                />
                {customFieldRows.length > 0 ? (
                    <GridItemList
                        titleId="custom-fields.title"
                        size={12}
                        data={customFieldRows}
                        key="employee-custom-fields"
                    />
                ) : null}
                {canReadPartnerPeerGroup ? (
                    <FeatureFlagged action="show" flag={FeatureFlag.PARTNER_PEER_GROUP}>
                        <GridItemList
                            titleId="employees.partnership-peer-group"
                            size={12}
                            key="employee-peer-group"
                            data={[peerGroupRow]}
                        />
                    </FeatureFlagged>
                ) : null}
                <HistoryCard titleId="employees.employment-history">
                    {employmentUnits.length > 0 ? (
                        <div className="p-4">{employmentUnits}</div>
                    ) : (
                        <div className="text-fg-secondary p-4">-</div>
                    )}
                </HistoryCard>
                <HistoryCard titleId="employees.affiliations-and-external-roles">
                    {affiliationUnits.length > 0 ? (
                        <div className="p-4">{affiliationUnits}</div>
                    ) : (
                        <div className="text-fg-secondary p-4">-</div>
                    )}
                </HistoryCard>
            </div>
            {auditInfo}
        </div>
    );
}
